import { AppNotification } from '../../state/NotificationContext';
import { notificationRepository, NotificationEntity } from '../../data/notificationRepository';
import { NOTIFICATIONS } from '../constants';
import { logger } from '../../infrastructure/logger';

const mapEntityToAppNotification = (entity: NotificationEntity): AppNotification => {
  // Map notification types
  const typeMap: Record<string, AppNotification['type']> = {
    'order': 'order',
    'review': 'system',
    'promo': 'promo',
    'system': 'system',
    'wishlist': 'system',
    'price_drop': 'promo',
    'follow': 'social'
  };

  // Convert ISO string to Date
  const timestamp = entity.createdAt ? new Date(entity.createdAt) : new Date();

  return {
    id: entity.id,
    title: entity.title,
    message: entity.message,
    type: typeMap[entity.type] || 'system',
    timestamp,
    read: entity.isRead,
    linkTo: entity.link
  };
};

const mapAppNotificationToEntity = (userId: string, notif: Omit<AppNotification, 'id' | 'timestamp' | 'read'>): Omit<NotificationEntity, 'id' | 'readAt'> => {
  return {
    userId,
    type: notif.type,
    title: notif.title,
    message: notif.message,
    link: notif.linkTo,
    data: {},
    isRead: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

export const notificationService = {

  // Polling-based notification fetching to reduce Firebase reads
  startPollingNotifications(userId: string, callback: (notifications: AppNotification[]) => void, intervalMs: number = NOTIFICATIONS.POLLING_INTERVAL_MS): () => void {
    if (!userId) {
      callback([]);
      return () => {};
    }

    const fetchData = async () => {
      try {
        const notifications = await notificationRepository.getUserNotifications(userId, 50);
        const appNotifications = notifications.map(mapEntityToAppNotification);
        callback(appNotifications);
      } catch (error) {
        logger.error('[NotificationService] Error polling notifications:', error);
        callback([]);
      }
    };

    // Initial fetch
    fetchData();

    // Poll every intervalMs (default 30 seconds) instead of real-time
    const intervalId = setInterval(fetchData, intervalMs);
    return () => clearInterval(intervalId);
  },

  async markAsRead(notificationId: string): Promise<void> {
    try {
      await notificationRepository.markAsRead(notificationId);
    } catch (error) {
      logger.error('[NotificationService] Error marking notification as read:', error);
      throw error;
    }
  },

  async markAllAsRead(userId: string): Promise<void> {
    if (!userId) return;
    try {
      await notificationRepository.markAllAsRead(userId);
    } catch (error) {
      logger.error('[NotificationService] Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Used by client-side logic to push a notification to the server
  // (e.g., when a local event happens that we want to persist)
  async createNotification(userId: string, notif: Omit<AppNotification, 'id' | 'timestamp' | 'read'>): Promise<void> {
    try {
      const entity = mapAppNotificationToEntity(userId, notif);
      await notificationRepository.create({
        ...entity,
        isRead: false
      } as NotificationEntity);
    } catch (error) {
      logger.error('[NotificationService] Error creating notification:', error);
      throw error;
    }
  },

  // Generates a local notification object (not persisted to DB immediately)
  createLocalNotification(data: Omit<AppNotification, 'id' | 'timestamp' | 'read'>): AppNotification {
    return {
      id: `local_${Date.now()}`,
      timestamp: new Date(),
      read: false,
      ...data
    };
  },

  // Additional methods using repository
  async countUnreadNotifications(userId: string): Promise<number> {
    try {
      return await notificationRepository.countUnread(userId);
    } catch (error) {
      logger.error('[NotificationService] Error counting unread notifications:', error);
      return 0;
    }
  },

  async deleteAllNotifications(userId: string): Promise<void> {
    try {
      await notificationRepository.deleteAllForUser(userId);
    } catch (error) {
      logger.error('[NotificationService] Error deleting all notifications:', error);
      throw error;
    }
  }
};

export default notificationService;
